import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

/** Properties of {@link ErrorBoundary}. */
export interface ErrorBoundaryProps {
  /** Renders the fallback for a caught error. */
  readonly fallback: (error: unknown) => ReactNode;
  /** Reports a caught error (without personal data), for example to the console. */
  readonly onError: (error: unknown, info: ErrorInfo) => void;
  /** Child elements. */
  readonly children: ReactNode;
}

interface ErrorBoundaryState {
  readonly error: unknown;
  readonly hasError: boolean;
}

/**
 * Catches rendering errors below it and shows a fallback instead of a blank page.
 * React still requires a class component for error boundaries.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  /** @inheritdoc */
  override state: ErrorBoundaryState = { error: null, hasError: false };

  /**
   * Stores the caught error.
   *
   * @param error - The error.
   * @returns The new state.
   */
  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    const state: ErrorBoundaryState = { error, hasError: true };

    return state;
  }

  /** @inheritdoc */
  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    this.props.onError(error, info);
  }

  /** @inheritdoc */
  override render(): ReactNode {
    const content = this.state.hasError
      ? this.props.fallback(this.state.error)
      : this.props.children;

    return content;
  }
}
